import type { QueryClient, InfiniteData } from "@tanstack/react-query";
import type { MediaSummary } from "@media-viewer/shared";

interface MediaListPage {
  items: MediaSummary[];
  nextCursor: string | null;
}

/**
 * Remove the given ids from every cached `["media", "list", ...]` infinite-
 * query entry. Lets a delete mutation update the visible grid instantly,
 * without waiting for a refetch round-trip. Untouched pages keep their
 * cursors, so infinite-scroll continues to work correctly.
 *
 * Pairs with `invalidateQueries({queryKey:["media"]})` for eventual
 * consistency — counts, other observers, and anything not in the
 * infinite-list cache shape.
 */
export function dropIdsFromMediaListCaches(
  qc: QueryClient,
  ids: number[],
): void {
  if (ids.length === 0) return;
  const idSet = new Set(ids);
  qc.setQueriesData<InfiniteData<MediaListPage>>(
    { queryKey: ["media", "list"] },
    (old) => {
      if (!old || !old.pages) return old;
      let touched = false;
      const pages = old.pages.map((p) => {
        const filtered = p.items.filter((it) => !idSet.has(it.id));
        if (filtered.length === p.items.length) return p;
        touched = true;
        return { ...p, items: filtered };
      });
      return touched ? { ...old, pages } : old;
    },
  );
}
